import axios from 'axios'

interface Envelope<T> {
    success: boolean
    data: T
    message?: string
}

export type PushTargetType = 'all' | 'project'
export type PushAppType = 'dispatcher' | 'driver' | 'both'

export interface PushTarget {
    type: PushTargetType
    appType: PushAppType
    projectName?: string
}

export interface PushPayload {
    title: string
    body: string
    data?: Record<string, unknown>
}

interface PreviewResult {
    deviceCount: number
    userCount: number
}

interface SendResult {
    total: number
    delivered: number
    failed: number
    logId: string
}

export interface HistoryItem {
    id: string
    sentAt: string
    sentBy: string
    target: PushTarget
    payload: PushPayload
    stats: {
        total: number
        delivered: number
        failed: number
    }
}

export type InactivityThreshold = '1' | '3' | '7' | '30'

export interface PushTemplate {
    title: string
    body: string
}

export interface TierConfig extends PushTemplate {
    enabled: boolean
}

export interface RetentionConfig {
    inactivity: {
        enabled: boolean
        tiers: Record<InactivityThreshold, TierConfig>
    }
    limitReset: {
        enabled: boolean
        template: PushTemplate
    }
    // Read-only, returned by GET: campaign key (inactivity_1, …, limit_reset) → ISO timestamp.
    lastSent?: Record<string, string>
}

interface InactivityTierResult {
    tier: string
    minDays: number
    maxDays: number
    enabled: boolean
    candidates: number
    reachable: number
    delivered: number
    failed: number
}

interface InactivityRunResult {
    enabled: boolean
    dryRun: boolean
    tiers: InactivityTierResult[]
}

export const pushNotificationsApi = {
    preview: async (target: PushTarget): Promise<PreviewResult> => {
        const res = await axios.post<Envelope<PreviewResult>>('/gluon/push-notifications/preview', { target })
        return res.data.data
    },

    send: async (target: PushTarget, payload: PushPayload): Promise<SendResult> => {
        const res = await axios.post<Envelope<SendResult>>('/gluon/push-notifications/send', { target, payload })
        return res.data.data
    },

    getHistory: async (): Promise<HistoryItem[]> => {
        const res = await axios.get<Envelope<{ items: HistoryItem[] }>>('/gluon/push-notifications/history')
        return res.data.data.items
    },

    getRetentionConfig: async (): Promise<RetentionConfig> => {
        const res = await axios.get<Envelope<RetentionConfig>>('/gluon/push-notifications/retention/config')
        return res.data.data
    },

    updateRetentionConfig: async (config: RetentionConfig): Promise<RetentionConfig> => {
        const res = await axios.put<Envelope<RetentionConfig>>('/gluon/push-notifications/retention/config', config)
        return res.data.data
    },

    runInactivityNow: async (dryRun = false): Promise<InactivityRunResult> => {
        const res = await axios.post<Envelope<InactivityRunResult>>('/gluon/push-notifications/retention/inactivity/run', { dryRun })
        return res.data.data
    },
}
